import { first, last } from 'lodash/fp';
import { useEffect, useState } from 'react';

import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import FrameTrigger from 'lib/clients/FrameTrigger';
import {
  SerializedButtonStyles,
  SerializedStepSelfieStyles,
  StepSelfieConfig,
} from 'lib/interfaces/StepsConfig';
import { SelfieMatch } from 'lib/interfaces/verificationConfig';
import { SelfiePoseOrder, SelfieStep } from 'verify/lib/interfaces/selfie';
import { SELFIE_TRIGGER_CONFIGS, SelfieTriggerType } from 'verify/scanners/SelfieScanner/client';
import { SelfieScannerBase } from 'verify/scanners/SelfieScanner/SelfieScanner';

const DEFAULT_PROFILE_SIZE = 250;

const SHARED_PREVIEW_STATES = [
  {
    trigger: new FrameTrigger(
      // initial trigger
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      last(SELFIE_TRIGGER_CONFIGS.filter((config) => config.type === SelfieTriggerType.Empty)),
    ),
    currentStep: SelfieStep.Center,
  },
  {
    trigger: new FrameTrigger(
      // delayed trigger
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      first(SELFIE_TRIGGER_CONFIGS.filter((config) => config.type === SelfieTriggerType.Empty)),
    ),
    currentStep: SelfieStep.Center,
  },
  {
    trigger: new FrameTrigger(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      SELFIE_TRIGGER_CONFIGS.find((config) => config.type === SelfieTriggerType.Center),
    ),
    currentStep: SelfieStep.Center,
  },
  {
    trigger: new FrameTrigger(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      SELFIE_TRIGGER_CONFIGS.find((config) => config.type === SelfieTriggerType.Center),
    ),
    currentStep: SelfieStep.Center,
  },
];

const PREVIEW_STATES = [
  ...SHARED_PREVIEW_STATES,
  {
    trigger: new FrameTrigger(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      SELFIE_TRIGGER_CONFIGS.find((config) => config.type === SelfieTriggerType.Left),
    ),
    currentStep: SelfieStep.Left,
  },
  {
    trigger: new FrameTrigger(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      SELFIE_TRIGGER_CONFIGS.find((config) => config.type === SelfieTriggerType.Right),
    ),
    currentStep: SelfieStep.Right,
  },
  {
    trigger: new FrameTrigger(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      SELFIE_TRIGGER_CONFIGS.find((config) => config.type === SelfieTriggerType.Verifying),
    ),
    currentStep: undefined,
  },
];

const CENTER_ONLY_PREVIEW_STATES = [
  ...SHARED_PREVIEW_STATES,
  {
    trigger: new FrameTrigger(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      SELFIE_TRIGGER_CONFIGS.find((config) => config.type === SelfieTriggerType.Verifying),
    ),
    currentStep: undefined,
  },
];

interface SelfieScannerEditorPreviewProps {
  assets: StepSelfieConfig['assets']['record-page'];
  localizations: StepSelfieConfig['localizations']['capture-page'];
  requireSelfieMatch: SelfieMatch;
  styles?: SerializedStepSelfieStyles;
  buttonStyles?: SerializedButtonStyles;
  featureFlags: FeatureFlagsMap;
}

const SelfieScannerEditorPreview = ({
  assets,
  localizations,
  requireSelfieMatch,
  styles,
  featureFlags,
}: SelfieScannerEditorPreviewProps) => {
  const previewStates =
    requireSelfieMatch === SelfieMatch.OnlyCenter ? CENTER_ONLY_PREVIEW_STATES : PREVIEW_STATES;
  const poseOrder: SelfiePoseOrder =
    requireSelfieMatch === SelfieMatch.OnlyCenter
      ? [SelfieStep.Center]
      : [SelfieStep.Center, SelfieStep.Left, SelfieStep.Right];

  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);

  useEffect(() => {
    const intervalPreviewStateTransition = setInterval(() => {
      setCurrentPreviewIndex((currentIndex) => currentIndex + 1);
    }, 2000);

    return () => {
      clearInterval(intervalPreviewStateTransition);
    };
  }, []);

  const { trigger, currentStep } = previewStates[currentPreviewIndex % previewStates.length];

  return (
    <SelfieScannerBase
      assets={assets}
      localizations={localizations}
      currentStep={currentStep}
      activeTrigger={trigger}
      videoWidthOverride={undefined}
      videoHeightOverride={undefined}
      setWebcamRef={() => undefined}
      videoConstraints={{}}
      handleUserMedia={() => undefined}
      handleFailed={() => undefined}
      handleManualCapture={() => undefined}
      manualVisible={true}
      profileSize={DEFAULT_PROFILE_SIZE}
      isEditorPreview={true}
      styles={styles}
      poseOrder={poseOrder}
      audioEnabled={false}
      enableMobilePendingExperienceForWeb={false}
      autoCaptureDisabled={false}
      featureFlags={featureFlags}
    />
  );
};

export default SelfieScannerEditorPreview;
